/**
 * CommunitySwitcherRail — the MY IMPACT rail. First adopter of the shared Rail
 * primitive (Rails Phase 1); its original job (a lateral switcher across the
 * communities you lead) has been superseded twice — by the §7.6 My Impact fold
 * and now by the §7.7 strict section split.
 *
 * ⚠ DEV-1458 / POLICY_NAVIGATION_RAILS §7.7 (Skip ruling 2026-07-12) — READ
 * BEFORE TOUCHING THE VISIBILITY GATE. This rail is My Impact's chrome and it
 * exists ONLY inside My Impact (`/memberships/*`). It is no longer a
 * "switcher": it does not persist across community pages, it does not claim
 * `/communities/*` or slug URLs, and it holds no active-community state.
 * Community pages are standalone, always. The name is kept only because the
 * file is referenced across the §7 family; its archetype is now 'section rail'.
 *
 * CONTENT (Slice E′, §7.3 v2 / §7.6): the Impact Signature card on top, then
 * the lens switcher — Memberships (belonging, DEFAULT) | Roles | Projects —
 * over lens-scoped search. The membership set is every DISTINCT community the
 * user belongs to, read from community_memberships (the single source
 * post-DEV-1372): geo declarations (home/operating) and org memberships
 * together, deduped per community (§8.10). The old two-lens (LEAD/MEMBER)
 * route-derived model retired with the /my-communities cards page.
 *
 * VISIBILITY: `/memberships/*` only (see isMyImpactSection), and only when the
 * membership set has 2+ distinct communities (a one-item rail has no value —
 * §8.10 rule 3). Dormant memberships (declared community inactive as a
 * community) display resolved to the nearest ACTIVE ancestor via the slice-2
 * landing walk (DEV-1413; DEV-1415 ruling condition 1) — the membership row is
 * never rewritten.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { TranslateFn } from '../../i18n/useAppTranslation';
import { usePersistentCollapse } from '../../hooks/usePersistentCollapse';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import { RAIL_NARROW_MAX } from './Rail';
import { useMyCommunities } from '../../Pages/Communities/useMyCommunities';
import { useMemberCommunities } from '../../Data/Membership/useMemberCommunities';
import type { SelectableEntry } from '../../Data/Engagement/engagementEntry';
import {
  communityInitial,
  type CommunityEntity,
  type CommunityRelationship,
} from '../../Pages/Communities/communityIdentity';
import { resolveEffectiveLogoUrl } from '../../api/communityMetadata';
import { isOrgEntityType } from '../../types/entityTypes';
import { isUserSystemAdmin } from '../../security/securityAdapter';
import CreateCommunityModal from '../CreateCommunityModal/CreateCommunityModal';
import ImpactSignatureCard, { ImpactSignatureHeaderTitle } from './ImpactSignatureCard';
import Rail from './Rail';
import RailSearch from './RailSearch';
import styles from './CommunitySwitcherRail.module.css';

const COLLAPSE_KEY = 'l4e_rail_communities_collapsed';

/** A community plus its nesting depth within its displayed type group. */
interface OrderedCommunity {
  entity: CommunityEntity;
  depth: number;
}

interface CommunityGroup {
  key: 'geo' | 'org' | 'other';
  label: string;
  items: OrderedCommunity[];
}

export interface CommunitySwitcherRailProps {
  entities?: CommunityEntity[];
  relationships?: CommunityRelationship[];
  currentUserGuid: string;
  apiCommunityRoles?: Record<string, any>[];
  /**
   * The user's engagement entries (composed once in App by the shared item
   * composer). §7.3 v2 (2026-07-09 late): they feed the ROLES and PROJECTS
   * pivot lenses (avatar-carrying rows → the main-pane role reference view)
   * and the Belonging counts — Belonging itself is a pure community map.
   */
  engagementEntries?: SelectableEntry[];
  /**
   * DEV-1446 (§7.6) — chunk B's Impact-Signature reveal predicate
   * (App.showImpactSurfaces). True → the compact signature card renders at
   * the top of the rail; false → the quiet locked slot.
   */
  impactRevealed?: boolean;
  /** §7.16 beat 2 — the arrival window (promoted, radar not yet explained):
   *  the signature card materializes with a one-shot animation. */
  impactArriving?: boolean;
}

/** Normalize a pathname for comparison: drop query/hash, lowercase, trim a
 *  trailing slash. */
function normalizePath(pathname: string): string {
  let x = (pathname || '').split('?')[0].split('#')[0].toLowerCase();
  if (x.length > 1 && x.endsWith('/')) x = x.slice(0, -1);
  return x;
}

/**
 * The My Impact section — DEV-1458 / §7.7 STRICT SECTION SPLIT (Skip ruling
 * 2026-07-12). The rail's visibility predicate is `/memberships/*` and NOTHING
 * ELSE: the section home (`/memberships`), the signature page
 * (`/memberships/impact`), and the role reference views
 * (`/memberships/role/:key`).
 *
 * What this deliberately NO LONGER matches — and must never match again:
 *   • `/communities` (the browse index)
 *   • `/communities/*` (canonical GUID detail URLs)
 *   • community SLUG URLs (the old community-URL-map gate, now deleted)
 *
 * A community page renders STANDALONE, always, regardless of entry point
 * (Discover Globe, search, feed link, deep link, refresh, or a click on a
 * membership row in this very rail). One URL, one chrome — chrome never
 * carries navigation history (§7.7 ruling 2; the entry-aware-chrome
 * alternative is explicitly NOT sanctioned). The defect this closes: visiting
 * a community you are NOT a member of used to show your personal Impact
 * Signature and memberships beside it.
 */
function isMyImpactSection(pathname: string): boolean {
  const p = normalizePath(pathname);
  return p === '/memberships' || p.startsWith('/memberships/');
}

/**
 * Order a set of communities by hierarchy (pre-order: a parent is immediately
 * followed by its descendants), and compute each one's nesting depth RELATIVE
 * to the others shown in the same group. Depth only counts ancestors that are
 * themselves displayed, so a community whose parent isn't in the list renders
 * flush-left rather than indented under an invisible parent.
 */
function orderByHierarchy(
  items: CommunityEntity[],
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
): OrderedCommunity[] {
  const shown = new Set(items.map((c) => c.entity_guid.toUpperCase()));

  // Nearest ancestor that is also in this group (cycle-safe).
  const nearestShownAncestor = (guid: string): string | null => {
    let cur = parentMap.get(guid.toUpperCase());
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      if (shown.has(cur)) return cur;
      cur = parentMap.get(cur);
    }
    return null;
  };

  const nameOf = (guid: string): string =>
    String(entityMap.get(guid)?.name || guid).toLowerCase();

  const depthCache = new Map<string, number>();
  const keyCache = new Map<string, string>();
  const depthOf = (guid: string): number => {
    const cached = depthCache.get(guid);
    if (cached != null) return cached;
    const anc = nearestShownAncestor(guid);
    const d = anc ? depthOf(anc) + 1 : 0;
    depthCache.set(guid, d);
    return d;
  };
  // Sort key threads each node under its nearest shown ancestor's key, so a
  // parent's whole subtree stays contiguous and ordered by name.
  const keyOf = (guid: string): string => {
    const cached = keyCache.get(guid);
    if (cached != null) return cached;
    const anc = nearestShownAncestor(guid);
    const k = (anc ? keyOf(anc) : '') + '/' + nameOf(guid);
    keyCache.set(guid, k);
    return k;
  };

  return items
    .map((c) => ({ entity: c, depth: depthOf(c.entity_guid.toUpperCase()) }))
    .sort((a, b) =>
      keyOf(a.entity.entity_guid.toUpperCase()).localeCompare(
        keyOf(b.entity.entity_guid.toUpperCase()),
      ),
    );
}

/**
 * Split the led communities into Geographic (geo_*) and Organizational (org_*)
 * groups (plus an Other fallback), each hierarchy-ordered. Empty groups are
 * dropped so the Rail only shows headers that have content.
 */
function groupCommunitiesByType(
  items: CommunityEntity[],
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
  t: TranslateFn,
): CommunityGroup[] {
  // Use the canonical classifiers, not ad-hoc prefix checks: org roots are
  // typed bare `organization` (only sub-levels carry the org_ prefix), so
  // isOrgEntityType (=== 'organization' || startsWith('org_')) is required —
  // otherwise Life4Earth Global / Rotary International fall through to "Other".
  const typeOf = (c: CommunityEntity) => (c.entity_type || '').toLowerCase();
  const geo = items.filter((c) => typeOf(c).startsWith('geo_'));
  const org = items.filter((c) => isOrgEntityType(typeOf(c)));
  const other = items.filter(
    (c) => !typeOf(c).startsWith('geo_') && !isOrgEntityType(typeOf(c)),
  );

  const groups: CommunityGroup[] = [];
  if (geo.length) {
    groups.push({ key: 'geo', label: t('rail.groupGeographic', 'Geographic'), items: orderByHierarchy(geo, entityMap, parentMap) });
  }
  if (org.length) {
    groups.push({ key: 'org', label: t('rail.groupOrganizational', 'Organizational'), items: orderByHierarchy(org, entityMap, parentMap) });
  }
  if (other.length) {
    groups.push({ key: 'other', label: t('rail.groupOther', 'Other'), items: orderByHierarchy(other, entityMap, parentMap) });
  }
  return groups;
}

export default function CommunitySwitcherRail({
  entities,
  relationships,
  currentUserGuid,
  apiCommunityRoles,
  engagementEntries,
  impactRevealed = false,
  impactArriving = false,
}: CommunitySwitcherRailProps) {
  const { t } = useAppTranslation('community');
  const { t: tPages } = useAppTranslation('pages');
  const navigate = useNavigate();
  const location = useLocation();

  // The Rail mounts app-wide (in the shell), but its hooks run on every route
  // before the visibility early-return below. Gate the membership computation
  // to the My Impact section by passing an empty userGuid off-section —
  // useV2CommunityRoles short-circuits on a falsy userGuid, so we don't fire a
  // permission-grants fetch on every authenticated page.
  //
  // DEV-1458 (§7.7): the community URL map (slug + GUID forms) that used to
  // widen this gate onto every community page is DELETED, not merely bypassed
  // — the section is the URL prefix, full stop.
  const onSection = isMyImpactSection(location.pathname);

  // The owner/admin set (V2 grants) — post-fold this powers the Roles lens's
  // Leadership authority chip and the Create-a-community gating, NOT a
  // separate lead lens (Slice E′: the two-lens model is retired; §7.2's
  // "Leadership roles" filter is SUBSUMED here per the 2026-07-09 ruling).
  const { myCommunities, myGuids, entityMap, parentMap } = useMyCommunities({
    entities,
    relationships,
    currentUserGuid: onSection ? currentUserGuid : '',
    apiCommunityRoles,
  });

  // Slice E′ — rail LENSES (ratified 2026-07-09): Belonging (DEFAULT — the
  // front door; a calm map of belonging) | Roles (pivot: flat scope-first
  // list of held roles, with the Leadership authority chip) | Projects
  // (pivot: role assignments by project). One record, alternate access
  // paths; pivots never become the default — this surface must not quietly
  // re-become the retired Engagements everything-hub.
  type RailLens = 'belonging' | 'roles' | 'projects';
  // DEV-1443 rule (third instance, Skip repro 2026-07-14): the lens
  // stickiness key MUST be user-namespaced — un-namespaced, a fresh
  // account on a shared browser inherited the PREVIOUS user's lens
  // (a new Contributor landed on an empty Roles lens). Legacy global
  // key is purged once. Stickiness itself is sanctioned (§7.9.7).
  const LENS_LS_KEY = `l4e_memberships_rail_lens:${String(currentUserGuid || 'anon').toUpperCase()}`;
  const readLens = (): RailLens => {
    try {
      const v = window.localStorage.getItem(LENS_LS_KEY);
      return v === 'roles' || v === 'projects' ? (v as RailLens) : 'belonging';
    } catch {
      return 'belonging';
    }
  };
  const [railLens, setRailLens] = useState<RailLens>(readLens);
  // Identity switch in the same session → re-read the NEW user's lens
  // (and drop the pre-DEV-1443 global key while we're here).
  useEffect(() => {
    try {
      window.localStorage.removeItem('l4e_memberships_rail_lens');
    } catch { /* ignore */ }
    setRailLens(readLens());
    // Intentional: keyed re-read on identity change only — readLens is
    // stable per LENS_LS_KEY, which derives from currentUserGuid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LENS_LS_KEY]);
  const pickLens = (l: RailLens) => {
    setRailLens(l);
    try {
      window.localStorage.setItem(LENS_LS_KEY, l);
    } catch {
      /* best-effort */
    }
  };
  // Leadership authority chip (Roles lens) — off by default.
  const [leadershipOnly, setLeadershipOnly] = useState(false);

  // Create-a-community (§7.5a): gated on an Administrator grant on ≥1 org
  // (sysadmins additionally get the relocated DEV-977 root-org entry).
  const administeredOrgs = useMemo(
    () =>
      myCommunities
        .filter((c) => isOrgEntityType((c.entity_type || '').toLowerCase()))
        .map((c) => ({ guid: c.entity_guid, name: c.name || '' })),
    [myCommunities],
  );
  const canCreateCommunity = administeredOrgs.length > 0;
  const isSysAdmin = isUserSystemAdmin(currentUserGuid);
  const [createOpen, setCreateOpen] = useState(false);

  // The belonging set — every DISTINCT community with an active membership row
  // (single source, §8.10 rule 4). DEV-1458: extracted to the shared
  // useMemberCommunities hook so this rail and the /memberships section home
  // (MembershipsHomePage) can never drift on "where do I belong?".
  const memberCommunities = useMemberCommunities({
    entities,
    relationships,
    currentUserGuid,
    enabled: onSection,
  });

  const displayed = memberCommunities;

  const { collapsed: userCollapsed, toggleCollapsed } = usePersistentCollapse(COLLAPSE_KEY, false);
  // Below the shared rail breakpoint the Rail force-collapses (Skip redline
  // 2026-07-09: content must ADAPT to the icon strip, not render crushed).
  // Same px constant the shared Rail chrome uses.
  const isNarrow = useIsNarrow(RAIL_NARROW_MAX);
  const collapsed = userCollapsed || isNarrow;

  // Group by type and order each group by hierarchy. Stable across renders so
  // the switcher doesn't reshuffle as the async data resolves.
  // Member lens (§7.3, Track 4 Slice C): Organizations BEFORE Places, with
  // the POLICY_ENABLER_VOCABULARY labels; lead lens keeps the Phase-1 order
  // until the Slice-E fold retires it.
  const groups = useMemo(() => {
    const base = groupCommunitiesByType(displayed, entityMap, parentMap, t);
    const relabeled = base.map((g) =>
      g.key === 'org'
        ? { ...g, label: t('rail.groupOrganizations', 'Organizations') }
        : g.key === 'geo'
          ? { ...g, label: t('rail.groupPlaces', 'Places') }
          : g,
    );
    const order: Record<CommunityGroup['key'], number> = { org: 0, geo: 1, other: 2 };
    return [...relabeled].sort((a, b) => order[a.key] - order[b.key]);
  }, [displayed, entityMap, parentMap, t]);

  // ── Memberships rail additions (§7.3, member lens only) ─────────────

  // Roles held per community (from the shared composer's engagement entries).
  const rolesByCommunity = useMemo(() => {
    const m = new Map<string, SelectableEntry[]>();
    for (const e of engagementEntries || []) {
      if (e.isPrevious) continue;
      const g = (e.groupCommunityGuid || '').toUpperCase();
      if (!g) continue;
      const arr = m.get(g) ?? [];
      arr.push(e);
      m.set(g, arr);
    }
    return m;
  }, [engagementEntries]);

  // The pinned "My Life4Earth" row RETIRED (architect ratification 2026-07-09
  // evening, §7.3/§7.6 amended): the Impact Signature card superseded it as
  // the user's platform-standing presence; /memberships/life4earth redirects
  // to the signature page; the pre-reveal locked slot is the Explorer's sole
  // standing presence.

  // Nested role rows REMOVED from Belonging (§7.3 v2 supersession, ratified
  // 2026-07-09 late): Belonging is a PURE map — rows navigate, counts stay
  // as passive richness signals; role access lives in the Roles lens (whose
  // rows now carry the owning entity's avatar).

  // Slice C″ — rail search (2026-07-09 review §3.6): filters communities,
  // role titles, and project scopes; searching reveals nested hits.
  const [railQ, setRailQ] = useState('');
  const railQNorm = railQ.trim().toLowerCase();
  const roleMatchesQ = useCallback(
    (e: SelectableEntry) =>
      !railQNorm ||
      e.roleLabel.toLowerCase().includes(railQNorm) ||
      (e.entityName || '').toLowerCase().includes(railQNorm),
    [railQNorm],
  );

  // Slice C′ (architect ratification 2026-07-09): role selection NAVIGATES to
  // the main-pane reference view (/memberships/role/:key), replacing the
  // retired MembershipTray. Active-row detection is route-derived,
  // GUID-case-insensitive (ruling 1).
  const activeMembershipRoleKey = useMemo(() => {
    const m = normalizePath(location.pathname).match(/^\/memberships\/role\/(.+)$/);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]).toUpperCase();
    } catch {
      return m[1].toUpperCase();
    }
  }, [location.pathname]);

  // Binding 1 (§7.3 v2 ratification): the route-active reveal lives in the
  // pivot lenses. Arriving on a role page (deep link, AC ?role= bridge)
  // while the rail shows Belonging switches to the Roles lens so the
  // highlighted row is visible. Arrivals from the Projects lens stay there —
  // its rows carry the same route-derived highlight.
  React.useEffect(() => {
    if (activeMembershipRoleKey && railLens === 'belonging') pickLens('roles');
    // Intentional: fires only when the ACTIVE ROLE changes. Including
    // railLens would yank a user back to Roles the moment they deliberately
    // switch to Belonging while a role page is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMembershipRoleKey]);

  // Slice C″ — role TYPE (2026-07-09 review §4: roles need a visual grammar;
  // small type labels, never color alone). Project roles by typeTag; org vs
  // civic by the owning community's family.
  const roleTypeOf = useCallback(
    (e: SelectableEntry): 'project' | 'org' | 'civic' => {
      if (e.typeTag === 'full-project' || e.typeTag === 'mini-project') return 'project';
      const g = (e.groupCommunityGuid || '').toUpperCase();
      const et = (entityMap.get(g)?.entity_type || '').toLowerCase();
      return et.startsWith('geo_') ? 'civic' : 'org';
    },
    [entityMap],
  );

  // Active community row — DEV-1458 as AMENDED (Skip, 2026-07-13). Membership
  // rows open the community IN-SECTION (/memberships/community/:guid), rail
  // intact, so switching between your places and organizations is one click —
  // the same grammar the Roles/Projects lenses use via /memberships/role/:key.
  //
  // The highlight is derived from the IN-SECTION route only. It deliberately
  // does NOT match /communities/:guid or slug URLs: those are the world's
  // standalone pages, this rail never renders beside them, and re-teaching it
  // to recognize them is what created the original defect. Same case-insensitive
  // GUID rule as activeMembershipRoleKey.
  const activeGuid = useMemo(() => {
    const m = normalizePath(location.pathname).match(
      /^\/memberships\/community\/([^/]+)/,
    );
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]).toUpperCase();
    } catch {
      return m[1].toUpperCase();
    }
  }, [location.pathname]);

  // Visibility gates (after hooks — hooks must run unconditionally).
  // §8.10 rule 3: hidden when the active lens's distinct set ≤ 1.
  if (!onSection) return null;
  if (displayed.length <= 1) return null;

  return (
    <>
    <Rail
      ariaLabel={t('rail.ariaLabel', 'Your communities')}
      // Memberships rail carries nested role cards + hierarchy indentation —
      // the sanctioned wider 'list' token (--rail-width-list; Skip redline
      // 2026-07-09). The lead lens (and its switcher width) retired in E′.
      widthVariant="list"
      // DEV-1446 redline batch 1 (architect-ratified 2026-07-09): no
      // generic header label. Skip redline 2026-07-10: the signature TITLE
      // moves up into the header row so the collapse toggle sits in line
      // with it (post-reveal only — pre-reveal the header is toggle-only).
      headerLabel={
        impactRevealed ? (
          <ImpactSignatureHeaderTitle onOpen={() => navigate('/memberships/impact')} />
        ) : undefined
      }
      collapsed={collapsed}
      onToggleCollapsed={() => toggleCollapsed()}
      footer={
        <>
          {!collapsed && (
            /* §7.3 — reinforce the obligations/belonging split: tasks live in
               the Action Center, this rail is belonging + reference. */
            <div className={styles.acHelper}>
              {t('rail.tasksHelper', 'Looking for tasks?')}{' '}
              <button
                type="button"
                className={styles.acHelperLink}
                onClick={() => navigate('/actions')}
              >
                {t('rail.openActionCenter', 'Open Action Center')}
              </button>
            </div>
          )}
          {/* Slice E′ / §7.5a ruling — renders ONLY for org administrators
              (V2 grant set) or sysadmins; creates CHILD orgs under an
              administered org via the community's own canonical creation
              surface. Root orgs stay sysadmin-gated (DEV-977, relocated
              here from the retired /my-communities cards page). */}
          {!collapsed && (canCreateCommunity || isSysAdmin) && (
            <button
              type="button"
              className={styles.viewAll}
              onClick={() => setCreateOpen(true)}
            >
              {t('rail.createCommunity', 'Create a community')}
            </button>
          )}
          {/* "View all communities" REMOVED (DEV-1446 redline batch 1) —
              the browse-all job moves to the Discover Community catalog
              (DEV-1447), which reuses the untouched community card
              component. Only this entry point is retired. */}
        </>
      }
    >
      <nav className={styles.list} aria-label={t('rail.ariaLabel', 'Your communities')}>
        {/* DEV-1446 (§7.6) — the compact Impact Signature card (post-reveal)
            or the quiet locked slot (pre-reveal Explorers), at the very top.
            Gateway only: click opens the full signature page in the main
            pane; the rail persists. */}
        {/* Collapsed rail keeps the signature as its TOP ICON (Skip redline
            2026-07-09): the compact mini-radar button — same gateway, icon
            density. Expanded rail gets the full identity card. */}
        <ImpactSignatureCard
          compact={collapsed}
          hideTitle
          revealed={impactRevealed}
          arriving={impactArriving}
          currentUserGuid={onSection && impactRevealed ? currentUserGuid : null}
          onOpen={() => navigate('/memberships/impact')}
        />
        {/* Slice E′ — the ratified lens switcher: Belonging (default) |
            Roles | Projects. Pivots over one record, never separate truth.
            Skip redline 2026-07-09: tabs ABOVE search — you choose what
            you're looking at, THEN search within it (search is lens-scoped,
            so it lives inside the lens context, not above it). */}
        {!collapsed && (
          <div className={styles.lensBar} role="tablist">
            {(['belonging', 'roles', 'projects'] as const).map((l) => (
              <button
                key={l}
                type="button"
                role="tab"
                aria-selected={railLens === l}
                className={`${styles.lensBtn} ${railLens === l ? styles.lensBtnOn : ''}`}
                onClick={() => pickLens(l)}
              >
                {t(`rail.lens.${l}`)}
              </button>
            ))}
          </div>
        )}
        {/* Lens-scoped search — DEV-1476: the chrome is now the shared
            RailSearch (one implementation in the codebase). The placeholder
            stays per-surface and names the ACTIVE LENS's scope, so nobody
            types a role name into the Memberships lens and thinks search is
            broken (Skip redline 2026-07-09). No `!collapsed` guard needed:
            RailSearch self-hides inside a collapsed Rail. */}
        <RailSearch
          value={railQ}
          onChange={setRailQ}
          placeholder={t(`rail.search.${railLens}`)}
        />
        {(railLens === 'belonging' || collapsed) && groups.map((group) => (
          <div
            key={group.key}
            className={styles.group}
            role="group"
            aria-label={group.label}
          >
            {!collapsed && <div className={styles.groupLabel}>{group.label}</div>}
            {group.items.map(({ entity: c, depth }) => {
              const guidUpper = c.entity_guid.toUpperCase();
              const isActive = activeGuid != null && guidUpper === activeGuid;
              const logoUrl = resolveEffectiveLogoUrl(c.entity_guid, entities, relationships);
              const name = c.name || t('rail.unnamed', '(unnamed)');
              const allRoles = rolesByCommunity.get(guidUpper) || [];
              // §7.3 v2: Belonging searches COMMUNITY NAMES only — role/
              // project queries get the honest cross-lens hint below the list
              // (binding 2), never a silent empty.
              if (railQNorm && !name.toLowerCase().includes(railQNorm)) return null;
              // Counts (C″ — review §3.3): passive richness signals.
              const projCount = allRoles.filter(
                (r) => r.typeTag === 'full-project' || r.typeTag === 'mini-project',
              ).length;
              const roleCount = allRoles.length - projCount;
              const communityBtn = (
                <button
                  type="button"
                  key={c.entity_guid}
                  className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
                  // IN-SECTION: the community opens in the main pane, this rail
                  // stays put (Skip 2026-07-13 — quick switching between your
                  // memberships is the point of the rail). Lands on the FEED
                  // (§6 participant lens; the tab deep-link rides the URL hash
                  // the detail page reads).
                  //
                  // Navigation OUT of My Impact to a community's standalone
                  // world page still happens — from Discover, from search, from
                  // a role page's "Open Community" button — and those correctly
                  // render clean, with no personal chrome.
                  onClick={() => navigate(`/memberships/community/${c.entity_guid}#feed`)}
                  title={name}
                  aria-current={isActive ? 'page' : undefined}
                  // Indent nested children relative to shown ancestors. Skipped
                  // when collapsed (the icon strip centers avatars).
                  style={
                    !collapsed && depth > 0
                      ? { paddingInlineStart: 8 + depth * 14 }
                      : undefined
                  }
                >
                  <span className={styles.avatar} aria-hidden="true">
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className={styles.avatarImg} />
                    ) : (
                      <span className={styles.avatarInitial}>{communityInitial(c.name)}</span>
                    )}
                  </span>
                  {!collapsed && (
                    <span className={styles.rowText}>
                      <span className={styles.rowName}>{name}</span>
                    </span>
                  )}
                  {!collapsed && allRoles.length > 0 && (
                    <span className={styles.rowCounts}>
                      {roleCount > 0
                        ? t('rail.roleCount', { count: roleCount, defaultValue: '{{count}} roles' })
                        : null}
                      {roleCount > 0 && projCount > 0 ? ' · ' : null}
                      {projCount > 0
                        ? t('rail.projectCount', { count: projCount, defaultValue: '{{count}} projects' })
                        : null}
                    </span>
                  )}
                  {/* Country flags removed from rail rows (Skip, 2026-07-02):
                      the custom community avatar carries identity here — a
                      trailing flag double-decorated every geo row. Flags
                      remain on disambiguation surfaces (search, browse, and
                      the slice-2 declaration picker). */}
                </button>
              );
              // §7.3 v2 (ratified 2026-07-09 late): Belonging is a PURE
              // map — no nested role rows, no expansion machinery. Role
              // access lives in the Roles lens (avatar-carrying rows).
              return communityBtn;
            })}
          </div>
        ))}
        {/* Binding 2 (§7.3 v2) — cross-lens search HONESTY: a role/project
            query typed in Belonging points at its lens (query preserved on
            switch), never a silent empty. Smallest honest behavior. */}
        {railLens === 'belonging' && !collapsed && railQNorm && (() => {
          const live = (engagementEntries || []).filter((e) => !e.isPrevious);
          const roleHits = live.filter(roleMatchesQ).length;
          const projHits = live.filter(
            (e) =>
              (e.typeTag === 'full-project' || e.typeTag === 'mini-project') &&
              roleMatchesQ(e),
          ).length;
          if (roleHits === 0) return null;
          return (
            <div className={styles.crossLensHints}>
              <button
                type="button"
                className={styles.crossLensHint}
                onClick={() => pickLens('roles')}
              >
                {t('rail.searchHintRoles', { count: roleHits, defaultValue: '{{count}} roles match — view in Roles' })}
              </button>
              {projHits > 0 && (
                <button
                  type="button"
                  className={styles.crossLensHint}
                  onClick={() => pickLens('projects')}
                >
                  {t('rail.searchHintProjects', { count: projHits, defaultValue: '{{count}} projects match — view in Projects' })}
                </button>
              )}
            </div>
          );
        })()}
        {/* ── Slice E′ — ROLES lens: flat, scope-first; the Leadership
            authority chip subsumes the §7.2 filter (2026-07-09 ruling). ── */}
        {railLens === 'roles' && !collapsed && (
          <div className={styles.group} role="group" aria-label={t('rail.lens.roles', 'Roles')}>
            <div className={styles.groupLabel}>{t('rail.myRoles', 'My roles')}</div>
            <button
              type="button"
              className={`${styles.authorityChip} ${leadershipOnly ? styles.authorityChipOn : ''}`}
              aria-pressed={leadershipOnly}
              onClick={() => setLeadershipOnly((v) => !v)}
            >
              {t('rail.leadershipChip', 'Leadership')}
            </button>
            {(() => {
              const all = (engagementEntries || []).filter((e) => !e.isPrevious);
              const filtered = all
                .filter((e) =>
                  leadershipOnly
                    ? myGuids.has((e.groupCommunityGuid || '').toUpperCase())
                    : true,
                )
                .filter(roleMatchesQ)
                .sort((a, b) => a.roleLabel.localeCompare(b.roleLabel));
              if (filtered.length === 0) {
                return <div className={styles.lensEmpty}>{t('rail.noRoles', 'No roles yet.')}</div>;
              }
              return filtered.map((r) => {
                const selected =
                  activeMembershipRoleKey != null &&
                  r.key.toUpperCase() === activeMembershipRoleKey;
                const rType = roleTypeOf(r);
                return (
                  <button
                    type="button"
                    key={r.key}
                    className={`${styles.roleCard} ${styles.roleCardFlat} ${selected ? styles.roleCardActive : ''}`}
                    onClick={() => navigate(`/memberships/role/${encodeURIComponent(r.key)}`)}
                    title={r.roleLabel}
                    aria-current={selected ? 'page' : undefined}
                  >
                    {/* §7.3 v2 — owning entity avatar carries the "where"
                        (replaces the abstract ◆ glyph; Skip 2026-07-09). */}
                    {(() => {
                      const og = r.groupCommunityGuid || r.owningCommunityGuid;
                      const logo = og ? resolveEffectiveLogoUrl(og, entities, relationships) : null;
                      return (
                        <span className={styles.roleEntityAvatar} aria-hidden="true">
                          {logo ? (
                            <img src={logo} alt="" className={styles.avatarImg} />
                          ) : (
                            <span className={styles.avatarInitial}>
                              {communityInitial(r.groupCommunityName || r.entityName)}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                    <span className={styles.roleCardMain}>
                      <span className={styles.roleCardTitle}>{r.roleLabel}</span>
                      <span className={styles.roleCardScope}>
                        {rType === 'project'
                          ? `${r.entityName}${r.groupCommunityName ? ` · ${r.groupCommunityName}` : ''}`
                          : r.groupCommunityName || r.entityName}
                      </span>
                    </span>
                    <span
                      className={`${styles.roleTypeTag} ${
                        rType === 'project'
                          ? styles.roleTypeProject
                          : rType === 'civic'
                            ? styles.roleTypeCivic
                            : styles.roleTypeOrg
                      }`}
                    >
                      {tPages(`memberships.roleType.${rType}`)}
                    </span>
                  </button>
                );
              });
            })()}
          </div>
        )}
        {/* ── Slice E′ — PROJECTS lens: role assignments pivoted by project.
            "Projects I'm working on" — never "Project Memberships" (the
            membership≠role lock); Belonging carries zero projects. ── */}
        {railLens === 'projects' && !collapsed && (
          <div className={styles.group} role="group" aria-label={t('rail.lens.projects', 'Projects')}>
            <div className={styles.groupLabel}>
              {t('rail.projectsLensLabel', "Projects I'm working on")}
            </div>
            {(() => {
              const projRoles = (engagementEntries || [])
                .filter(
                  (e) =>
                    !e.isPrevious &&
                    (e.typeTag === 'full-project' || e.typeTag === 'mini-project'),
                )
                .filter(roleMatchesQ)
                .sort(
                  (a, b) =>
                    a.entityName.localeCompare(b.entityName) ||
                    a.roleLabel.localeCompare(b.roleLabel),
                );
              if (projRoles.length === 0) {
                return (
                  <div className={styles.lensEmpty}>{t('rail.noProjects', 'No projects yet.')}</div>
                );
              }
              return projRoles.map((r) => {
                const selected =
                  activeMembershipRoleKey != null &&
                  r.key.toUpperCase() === activeMembershipRoleKey;
                return (
                  <button
                    type="button"
                    key={r.key}
                    className={`${styles.roleCard} ${styles.roleCardFlat} ${selected ? styles.roleCardActive : ''}`}
                    onClick={() => navigate(`/memberships/role/${encodeURIComponent(r.key)}`)}
                    title={r.entityName}
                    aria-current={selected ? 'page' : undefined}
                  >
                    {/* §7.3 v2 — project identity: hero thumb when present,
                        else the owning community's logo. */}
                    {(() => {
                      const og = r.groupCommunityGuid || r.owningCommunityGuid;
                      const img =
                        r.heroUrl ||
                        (og ? resolveEffectiveLogoUrl(og, entities, relationships) : null);
                      return (
                        <span className={styles.roleEntityAvatar} aria-hidden="true">
                          {img ? (
                            <img src={img} alt="" className={styles.avatarImg} />
                          ) : (
                            <span className={styles.avatarInitial}>
                              {communityInitial(r.entityName)}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                    <span className={styles.roleCardMain}>
                      <span className={styles.roleCardTitle}>{r.entityName}</span>
                      <span className={styles.roleCardScope}>
                        {r.roleLabel}
                        {r.groupCommunityName
                          ? ` · ${t('rail.ownedBy', 'Owned by {{name}}', { name: r.groupCommunityName })}`
                          : ''}
                      </span>
                    </span>
                  </button>
                );
              });
            })()}
          </div>
        )}
      </nav>
    </Rail>
    <CreateCommunityModal
      isOpen={createOpen}
      onClose={() => setCreateOpen(false)}
      administeredOrgs={administeredOrgs}
      isSysAdmin={isSysAdmin}
      onPickOrg={(guid) => {
        setCreateOpen(false);
        // One-shot state → CommunityDetailPage opens Settings on arrival
        // (child-org creation lives in Settings → Entities, the canonical
        // taxonomy-aware surface).
        navigate(`/communities/${guid}`, { state: { openCommunitySettings: true } });
      }}
    />
    </>
  );
}
